import React, { ReactElement, useCallback } from "react";
import { ApolloError } from "@apollo/client";

import { useAuth } from "../../../context/AuthContext";
import { useGetUserByIdQuery } from "../../../graphql/queries/get-user-by-id.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import NetworkStatusAlert from "components/network-status-alert/network-status-alert";
import { useNetworkStatus } from "context/NetworkStatusContext";
import LoggedNavigationLayout from "components/layouts/logged-navigation-layout/logged-navigation-layout";
import AnonNavigationLayout from "components/layouts/anon-navigation-layout/anon-navigation-layout";

interface LayoutManagerProps {
	showNetworkStatusAlert?: boolean;
}

const LayoutManager = ({ showNetworkStatusAlert }: LayoutManagerProps): ReactElement => {
	const { loggedUser } = useAuth();
	const { updateLoggedUser, loggedUserValue } = useLoggedUser();
	const { isOffline } = useNetworkStatus();

	const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
		console.log("LayoutManager Error Get Users: ", error);
		// NetworkStatusContext automatically handles this error through Apollo error link
	}, []);

	const handleOnCompletedGetUsers = useCallback(
		data => {
			// NetworkStatusContext automatically tracks successful operations
			const { users_by_pk } = data;
			if (users_by_pk[0]) {
				// In this case, we're getting data from the server
				// The updateLoggedUser function will preserve selected properties
				updateLoggedUser(users_by_pk[0]);
			}
		},
		[updateLoggedUser]
	);

	const { data, error, loading, refetch } = useGetUserByIdQuery({
		skip: !loggedUser?.uid,
		variables: {
			id: loggedUser?.uid,
			is_not_deleted: true
		},
		errorPolicy: "all",
		fetchPolicy: "network-only",
		nextFetchPolicy: "cache-first", // Use cache after successful fetch
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorsGetUsers,
		onCompleted: handleOnCompletedGetUsers
	});

	// Verify if the Firebase user matches our stored user
	if (loggedUserValue && loggedUserValue.id && loggedUserValue.id !== loggedUser.uid) {
		console.error("Firebase user mismatch! Login again");
	}

	// Determine user data source (fresh from server or cached)
	const userData = data?.users_by_pk[0] || loggedUserValue;
	const usingCachedData = !data?.users_by_pk[0] && !!loggedUserValue && !loading;

	// Handle errors - only redirect to login for non-network errors when we have no cached data
	if (error && !userData) {
		const isNetworkError =
			!!error.networkError ||
			error.message?.includes("timeout") ||
			error.message?.includes("fetch") ||
			error.message?.includes("Network");

		if (!isNetworkError) {
			console.error("Error fetching user data: ", error);
		}
	}

	// Determine which layout to render based on user authentication status
	const LayoutComponent = userData?.id ? LoggedNavigationLayout : AnonNavigationLayout;

	return (
		<>
			{showNetworkStatusAlert && (
				<NetworkStatusAlert visible={isOffline || usingCachedData} onRetry={refetch} loading={loading} />
			)}
			<LayoutComponent />
		</>
	);
};

export default LayoutManager;
